import { User } from "@/common/types/user";
import { Permission } from "@api/shared-runtime/enums";

export { Permission };

/**
 * Hook to check user permissions.
 * Admins automatically have all permissions.
 */
export function usePermissions(user: User | null | undefined) {
  const hasPermission = (permissionName: Permission): boolean => {
    if (!user) {
      return false;
    }
    if (user.is_admin) {
      return true;
    }
    return (
      user.permissions?.includes(permissionName) ||
      user.globalPermissions?.includes(permissionName) ||
      false
    );
  };

  const hasAnyPermission = (permissionNames: Permission[]): boolean => {
    if (!user) {
      return false;
    }
    if (user.is_admin) {
      return true;
    }
    return permissionNames.some(
      (p) =>
        user.permissions?.includes(p) ||
        user.globalPermissions?.includes(p) ||
        false,
    );
  };

  const canViewLeads = hasPermission(Permission.ViewLeads);
  const canEditLeads = hasPermission(Permission.EditLeads);

  // Check if user has admin access OR any admin-section permission
  const canAccessAdminSection =
    user?.is_admin ||
    hasAnyPermission([
      Permission.ViewLeads,
      Permission.EditLeads,
      Permission.AppManageProviderPayouts,
      Permission.AppChargeOrganizationInvoices,
      Permission.AppImpersonateUsers,
    ]);

  return {
    hasPermission,
    hasAnyPermission,
    canViewLeads,
    canEditLeads,
    canAccessAdminSection,
  };
}
